import { Alert, Stack, TextField, Button, Link } from "@mui/material"
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { IApiService } from "../../services/ApiService";
import { PasswordInput } from "../../components/PasswordInput";
import { LogosHeader } from "../../components/LogosHeader";
import { authNavigationService } from "../../services/AuthNavigationService";
import { Logger } from '../../utils/Logger';

export interface LoginPageProps {
    readonly api: IApiService;
}

export const LoginPage = (props: LoginPageProps) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string | undefined>();
    const [password, setPassword] = useState<string | undefined>();
    const [inProgress, setInProgress] = useState<boolean>(false);
    const [errors, setErrors] = useState<Readonly<{[key: string]: ReadonlyArray<string>}> | undefined>(undefined);
    const [networkError, setNetworkError] = useState<string | undefined>(undefined);
    const formEnabled = email?.trim() && password?.trim() && !inProgress;

    // Auto-redirect if user is authenticated (e.g., by useAppInitialization calling getUserDetail)
    // This handles the case where the user already has a valid session
    useEffect(() => {
        if (props.api.isAuthenticated()) {
            Logger.debug('LoginPage', 'User is already authenticated, redirecting to post-login URL');
            const redirectUrl = authNavigationService.getPostLoginRedirect();
            navigate(redirectUrl, { replace: true });
        }
    }, [props.api, navigate]);
    const handleClick = async() => {
        if (!formEnabled) {
            return;
        }
        if (!email?.trim() || !password?.trim()) {
            throw new Error("Unexpected");
        }
        setErrors(undefined);
        setNetworkError(undefined);
        setInProgress(true);
        const controller = new AbortController();
        try {
            const errors = await props.api.login(email.trim(), password.trim(), controller.signal);
            if (errors) {
                setErrors(errors);
            } else {
                const redirectUrl = authNavigationService.getPostLoginRedirect();
                navigate(redirectUrl, { replace: true });
            }
        } catch (e) {
            setNetworkError("Impossible de se connecter. Vérifiez votre connexion.");
        } finally {
            setInProgress(false);
        }
    };
    return (
        <Stack sx={{p: 2, pt: 4}} spacing={2}>
            <LogosHeader sx={{mb: 4}} />
            <Stack spacing={2}>
                {networkError && <Alert severity="error">{networkError}</Alert>}
                {errors && <Alert severity="error">
                    {Object.entries(errors).map(x => x[1].map(error => <>
                        {error}<br/>
                    </>))}
                </Alert>}
                <TextField
                    onChange={(ev) => setEmail(ev.currentTarget.value)}
                    margin="normal"
                    required
                    fullWidth
                    label="Adresse email"
                    autoComplete="email"
                    autoFocus
                />
                <PasswordInput
                    label="Mot de passe"
                    password={password}
                    handlePassword={(ev) => setPassword(ev.currentTarget.value)}
                    autoComplete="current-password"
                />
                <Button
                    onClick={() => handleClick()}
                    disabled={inProgress}
                    type="submit"
                    fullWidth
                    variant="contained">
                    Connexion
                </Button>
                <Link href="/account/forgot-password" variant="body2">
                    Mot de passe perdu ou première connexion ?
                </Link>
            </Stack>
        </Stack>
    );
}
